"use client"; // <-- to make sure we can mount the Provider from a server component
import type { AppRouter } from "@newform-monorepo/backend/src/router";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import { httpBatchStreamLink } from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server";
import { useState } from "react";
import superjson from "superjson";
import { makeQueryClient } from "./query-client";

export const trpc = createTRPCReact<AppRouter>();
export type RouterOutput = inferRouterOutputs<AppRouter>;
export type RouterInput = inferRouterInputs<AppRouter>;
let clientQueryClientSingleton: QueryClient;
function getQueryClient() {
  if (typeof window === "undefined") {
    // Server: always make a new query client
    return makeQueryClient();
  }
  // Browser: use singleton pattern to keep the same query client
  // biome-ignore lint/suspicious/noAssignInExpressions: singleton pattern requires assignment in expression
  return (clientQueryClientSingleton ??= makeQueryClient());
}
export function getClientSideCookie(name: string): string | undefined {
  const cookieValue = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
    ?.split("=")[1];

  return cookieValue;
}

function getUrl() {
  const base = (() => {
    if (process.env.NEXT_PUBLIC_NEWFORM_API_DOMAIN)
      return process.env.NEXT_PUBLIC_NEWFORM_API_DOMAIN;
    return "http://localhost:3001";
  })();
  return base;
}
export function TRPCProvider(
  props: Readonly<{
    children: React.ReactNode;
  }>,
) {
  // NOTE: Avoid useState when initializing the query client if you don't
  //       have a suspense boundary between this and the code that may
  //       suspend because React will throw away the client on the initial
  //       render if it suspends and there is no boundary
  const queryClient = getQueryClient();
  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        httpBatchStreamLink({
          transformer: superjson, // <-- if you use a data transformer
          url: getUrl(),
          // Batched queries are sent as ONE GET with every input serialized
          // into the URL. Node rejects requests whose headers exceed 16KB
          // (HTTP 431) before any route code runs, and the creator board has
          // already hit that ceiling (~300 creator ids in one batch). Splitting
          // batches at 8KB leaves ample room for cookies/auth headers.
          maxURLLength: 8_000,
          fetch(url, options) {
            return fetch(url, {
              ...options,
              credentials: "include",
              signal: AbortSignal.timeout(300000), // 5 minute timeout
              headers: {
                ...(options?.headers || {}),
                authorization: getClientSideCookie("session") || "",
              },
            } as RequestInit);
          },
        }),
      ],
    }),
  );
  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        {props.children}
      </QueryClientProvider>
    </trpc.Provider>
  );
}
